/**
 * O que faz: uma linha do passo "Entendimento": mostra quando a coluna será usada, confirma ("Está certo") ou
 *            abre os ajustes ("Quero ajustar") com escolhas em português, sem prompt, enum, UUID ou confiança.
 * Onde usa: ConfiguracaoOrganizacaoKanban (passo 2 do assistente).
 * Conexões: catálogo e funções puras de rascunho.ts; GavetaSuave para a área de ajustes.
 * Tabelas: nenhuma; só altera o rascunho local do pai.
 * Data: 21/10/2026.
 */
import { useId, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { GavetaSuave } from '@/components/ui/gaveta-suave';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { LIMITES_ORGANIZACAO, type CondicaoColuna, type ExigenciaRegistro, type RegraColuna } from './contratos';
import { regraComCondicao, regraPronta } from './rascunho';

// Frases da clínica, não nomes técnicos: cada uma mapeia para uma condição fechada do catálogo.
const OPCOES_SIGNIFICADO: Array<{ valor: CondicaoColuna; rotulo: string }> = [
  { valor: 'interesse', rotulo: 'Demonstra interesse em um tratamento' },
  { valor: 'preco', rotulo: 'Pergunta ou negocia valores' },
  { valor: 'quer_marcar', rotulo: 'Diz que quer marcar uma avaliação' },
  { valor: 'retomar', rotulo: 'Pede para a clínica falar com ele depois' },
  { valor: 'personalizado', rotulo: 'Explicar com minhas palavras' },
  { valor: 'fato_sistema', rotulo: 'Somente pelos registros do CRM' },
];
const OPCOES_REGISTRO: Array<{ valor: Exclude<ExigenciaRegistro, null>; rotulo: string }> = [
  { valor: 'agenda', rotulo: 'Consulta marcada no CRM' },
  { valor: 'comparecimento', rotulo: 'Comparecimento registrado' },
  { valor: 'venda', rotulo: 'Venda registrada' },
];
const NAO_ORGANIZAR = 'nao_organizar';

interface EditorRegraColunaProps {
  regra: RegraColuna;
  onAlterar: (regra: RegraColuna) => void;
  // "Não organizar esta coluna" tira a coluna do rascunho; a coluna e seus cards continuam no Pipeline.
  onNaoOrganizar?: () => void;
  // Outras colunas, para o ajuste opcional de origem (de onde o card pode vir).
  colunasOrigem?: Array<{ id: string; nome: string }>;
}

export function EditorRegraColuna({ regra, onAlterar, onNaoOrganizar, colunasOrigem = [] }: EditorRegraColunaProps) {
  const idAjustes = useId();
  const idOrigens = useId();
  const pronta = regraPronta(regra);
  // Regra sem significado já abre os ajustes: a clínica precisa escolher antes de confirmar.
  const [ajustando, setAjustando] = useState(() => !pronta);
  const [origensAbertas, setOrigensAbertas] = useState(false);
  // Nome desconhecido começa sem opção marcada; "Explicar com minhas palavras" só aparece se a pessoa escolher.
  const [explicando, setExplicando] = useState(regra.condicao === 'personalizado' && regra.descricao.length > 0);
  const valorSelecionado = regra.condicao === 'personalizado' && !explicando ? '' : regra.condicao;

  const escolherSignificado = (valor: string) => {
    if (valor === NAO_ORGANIZAR) { onNaoOrganizar?.(); return; }
    const condicao = valor as CondicaoColuna;
    setExplicando(condicao === 'personalizado');
    onAlterar(regraComCondicao(regra, condicao));
  };
  const confirmar = () => {
    // Validação: só confirma regra completa; o servidor recusa regra sem significado ou sem registro do CRM.
    if (!pronta) return;
    onAlterar({ ...regra, confirmada: true });
    setAjustando(false);
  };
  const alternarOrigem = (etapaId: string, marcada: boolean) => {
    const origens = marcada ? [...regra.origensPermitidas, etapaId] : regra.origensPermitidas.filter((id) => id !== etapaId);
    onAlterar({ ...regra, origensPermitidas: origens, confirmada: false });
  };
  const outrasColunas = colunasOrigem.filter((coluna) => coluna.id !== regra.etapaId);

  return (
    <section aria-label={`Regra da coluna ${regra.nome}`} className="space-y-3 rounded-xl border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 break-words text-sm font-semibold text-foreground">{regra.nome}</p>
        <span className={cn('lg-selo transition-colors duration-200', regra.confirmada ? 'lg-selo--sucesso' : 'lg-selo--neutro')}>
          {regra.confirmada ? 'Confirmado' : 'Falta confirmar'}
        </span>
      </div>
      <p className="text-sm text-foreground">
        Vamos usar esta coluna quando: <span className="font-medium">{regra.descricao || 'você escolher o significado abaixo.'}</span>
      </p>
      {regra.exclusoes.length > 0 && (
        <p className="text-xs text-muted-foreground">Não vale quando: {regra.exclusoes.join(' ')}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" className="rounded-full" disabled={!pronta || regra.confirmada} onClick={confirmar}>
          <Check className="mr-1 h-4 w-4" aria-hidden="true" />Está certo
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-full" aria-expanded={ajustando} aria-controls={idAjustes}
          onClick={() => setAjustando((valor) => !valor)}>
          Quero ajustar
          <ChevronDown className={cn('ml-1 h-4 w-4 transition-transform duration-200 motion-reduce:transition-none', ajustando && 'rotate-180')} aria-hidden="true" />
        </Button>
      </div>
      {!pronta && regra.condicao === 'fato_sistema' && (
        <p className="text-xs text-warning-text">Escolha qual registro do CRM leva o card para esta coluna.</p>
      )}

      <GavetaSuave aberta={ajustando} id={idAjustes} className="space-y-3 pt-1">
        <RadioGroup value={valorSelecionado} onValueChange={escolherSignificado} aria-label={`O que significa a coluna ${regra.nome}`} className="gap-2">
          {OPCOES_SIGNIFICADO.map((opcao) => (
            <div key={opcao.valor} className="flex items-center gap-2">
              <RadioGroupItem value={opcao.valor} id={`${idAjustes}-${opcao.valor}`} />
              <Label htmlFor={`${idAjustes}-${opcao.valor}`} className="font-normal">{opcao.rotulo}</Label>
            </div>
          ))}
          {onNaoOrganizar && (
            <div className="flex items-center gap-2">
              <RadioGroupItem value={NAO_ORGANIZAR} id={`${idAjustes}-${NAO_ORGANIZAR}`} />
              <Label htmlFor={`${idAjustes}-${NAO_ORGANIZAR}`} className="font-normal">Não organizar esta coluna</Label>
            </div>
          )}
        </RadioGroup>

        {regra.condicao === 'personalizado' && explicando && (
          <div className="space-y-1">
            <Label htmlFor={`${idAjustes}-descricao`}>Quando um contato deve ir para esta coluna?</Label>
            <Textarea
              id={`${idAjustes}-descricao`}
              value={regra.descricao}
              maxLength={LIMITES_ORGANIZACAO.descricao}
              placeholder="Ex.: o paciente informa o convênio e pergunta se a clínica atende."
              onChange={(evento) => onAlterar({ ...regra, descricao: evento.target.value.slice(0, LIMITES_ORGANIZACAO.descricao), confirmada: false })}
            />
            <p className="text-right text-[11px] text-muted-foreground">{regra.descricao.length}/{LIMITES_ORGANIZACAO.descricao}</p>
          </div>
        )}

        {regra.condicao === 'fato_sistema' && (
          <RadioGroup
            value={regra.exigeRegistro ?? ''}
            onValueChange={(valor) => onAlterar(regraComCondicao(regra, 'fato_sistema', valor as Exclude<ExigenciaRegistro, null>))}
            aria-label="Registro do CRM que leva o card para esta coluna"
            className="gap-2 rounded-lg bg-muted p-3"
          >
            {OPCOES_REGISTRO.map((opcao) => (
              <div key={opcao.valor} className="flex items-center gap-2">
                <RadioGroupItem value={opcao.valor} id={`${idAjustes}-registro-${opcao.valor}`} />
                <Label htmlFor={`${idAjustes}-registro-${opcao.valor}`} className="font-normal">{opcao.rotulo}</Label>
              </div>
            ))}
          </RadioGroup>
        )}

        {outrasColunas.length > 0 && (
          <div className="space-y-2">
            <Button type="button" variant="ghost" size="sm" className="h-auto px-0 text-xs" aria-expanded={origensAbertas} aria-controls={idOrigens}
              onClick={() => setOrigensAbertas((valor) => !valor)}>
              Ajustar de quais colunas o card pode vir
              <ChevronDown className={cn('ml-1 h-3 w-3 transition-transform duration-200 motion-reduce:transition-none', origensAbertas && 'rotate-180')} aria-hidden="true" />
            </Button>
            <GavetaSuave aberta={origensAbertas} id={idOrigens} className="space-y-2">
              <p className="text-xs text-muted-foreground">Sem nenhuma marcada, o card pode vir de qualquer coluna.</p>
              {outrasColunas.map((coluna) => (
                <div key={coluna.id} className="flex items-center gap-2">
                  <Checkbox id={`${idOrigens}-${coluna.id}`} checked={regra.origensPermitidas.includes(coluna.id)}
                    onCheckedChange={(marcada) => alternarOrigem(coluna.id, marcada === true)} />
                  <Label htmlFor={`${idOrigens}-${coluna.id}`} className="font-normal">{coluna.nome}</Label>
                </div>
              ))}
            </GavetaSuave>
          </div>
        )}
      </GavetaSuave>
    </section>
  );
}
